import React, { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, Printer, FileText, List, ChevronRight } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface DocumentViewerProps {
  content: string;
  title: string;
  filename: string;
}

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  content,
  title,
  filename,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showToc, setShowToc] = useState(true);
  const [activeSection, setActiveSection] = useState<string>("");

  // Extract table of contents from markdown content
  const tableOfContents = useMemo(() => {
    const toc: TableOfContentsItem[] = [];
    const lines = content.split("\n");

    lines.forEach((line, index) => {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .trim();

        toc.push({ id, text, level });
      }
    });

    return toc;
  }, [content]);

  // Scroll to top when component mounts or content changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [content, title]);

  // Handle intersection observer for active section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -35% 0px",
        threshold: 0,
      }
    );

    const headings = document.querySelectorAll(
      "h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]"
    );
    headings.forEach((heading) => observer.observe(heading));

    return () => {
      headings.forEach((heading) => observer.unobserve(heading));
    };
  }, [content]);

  const handleTocClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    setIsLoading(true);
    try {
      const element = document.getElementById("document-content");
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const createHeadingComponent = (level: number) => {
    return ({ children, ...props }: any) => {
      const text =
        typeof children === "string" ? children : children?.toString() || "";
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();

      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      const className =
        level === 1
          ? "legal-heading"
          : level === 2
          ? "legal-subheading"
          : "legal-section";

      return (
        <HeadingTag id={id} className={className} {...props}>
          {children}
        </HeadingTag>
      );
    };
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      {/* Header with Actions */}
      <div className="no-print mb-4 sm:mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-3 lg:mb-0">
          <FileText className="text-primary-500 flex-shrink-0" size={20} />
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
            {title}
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => setShowToc(!showToc)}
            className="btn-secondary flex items-center justify-center space-x-2 text-sm px-3 py-2"
          >
            <List size={14} />
            <span className="hidden sm:inline">
              {showToc ? "Hide" : "Show"} Contents
            </span>
            <span className="sm:hidden">TOC</span>
          </button>
          <button
            onClick={handlePrint}
            className="btn-secondary flex items-center justify-center space-x-2 text-sm px-3 py-2"
          >
            <Printer size={14} />
            <span>Print</span>
          </button>
          <button
            onClick={handleExportPDF}
            disabled={isLoading}
            className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 text-sm px-3 py-2"
          >
            <Download size={14} />
            <span>{isLoading ? "Generating..." : "Export PDF"}</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Table of Contents Sidebar */}
        {showToc && tableOfContents.length > 0 && (
          <div className="no-print w-full lg:w-80 lg:flex-shrink-0 order-2 lg:order-1">
            <div className="lg:sticky lg:top-6 bg-white border border-gray-200 rounded-lg shadow-sm p-3 sm:p-4 max-h-60 lg:max-h-[calc(100vh-8rem)] overflow-y-auto">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center">
                <List size={18} className="mr-2 text-primary-500" />
                <span className="hidden sm:inline">Table of Contents</span>
                <span className="sm:hidden">Contents</span>
              </h3>
              <nav className="space-y-1">
                {tableOfContents.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleTocClick(item.id)}
                    className={`
                      w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm transition-colors duration-200
                      ${
                        item.level === 1
                          ? "font-semibold"
                          : item.level === 2
                          ? "font-medium"
                          : "font-normal"
                      }
                      ${
                        activeSection === item.id
                          ? "bg-primary-50 text-primary-700 border-l-2 border-primary-500"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      }
                    `}
                    style={{ paddingLeft: `${Math.min(item.level * 8, 24)}px` }}
                  >
                    <div className="flex items-center">
                      {item.level > 1 && (
                        <ChevronRight
                          size={12}
                          className="mr-1 flex-shrink-0 opacity-50"
                        />
                      )}
                      <span className="truncate">{item.text}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Document Content */}
        <div className="flex-1 min-w-0 order-1 lg:order-2">
          <div
            id="document-content"
            className="legal-paper px-4 sm:px-6 lg:px-8"
          >
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: createHeadingComponent(1),
                  h2: createHeadingComponent(2),
                  h3: createHeadingComponent(3),
                  h4: createHeadingComponent(4),
                  h5: createHeadingComponent(5),
                  h6: createHeadingComponent(6),
                  p: ({ children }) => <p className="legal-text">{children}</p>,
                  ul: ({ children }) => (
                    <ul className="legal-list">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="legal-list list-decimal">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-gray-700 leading-relaxed">
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-900">
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-gray-800">{children}</em>
                  ),
                  hr: () => <hr className="my-6 border-gray-300" />,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-600 my-4">
                      {children}
                    </blockquote>
                  ),
                  code: ({ children }) => (
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto my-4">
                      {children}
                    </pre>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-6">
                      <table className="min-w-full border border-gray-300">
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border border-gray-300 px-4 py-2 bg-gray-50 font-semibold text-left">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-300 px-4 py-2">
                      {children}
                    </td>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
